/**
 * DesignsList — the "saved calculations" landing page for a single module.
 *
 * Drop-in component: every module's index route renders this with its
 * own `module` slug. Shows search bar, "new" button, card grid with the
 * saved designs, and pagination at the bottom.
 *
 * Clicking a card navigates to `/app/{module}/{id}` so the design page
 * can hydrate from the saved payload. Clicking "Nuevo" navigates to
 * `/app/{module}/new`.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDeleteDesign, useDesignsList } from '@/features/designs/useDesigns';
import { type DesignModule, MODULE_LABELS } from '@/features/designs/types';

interface DesignsListProps {
  module: DesignModule;
  /** Right-side description shown under the page title. */
  description?: string;
  /** Optional icon for the empty-state illustration / page chrome. */
  icon?: React.ReactNode;
}

export function DesignsList({ module, description, icon }: DesignsListProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null);

  const listQuery = useDesignsList({ module, q, page, per_page: 12 });
  const del = useDeleteDesign();

  const data = listQuery.data;
  const items = data?.items ?? [];
  const pages = data?.pages ?? 1;
  const total = data?.total ?? 0;

  return (
    <div className="px-8 py-8">
      {/* ----- Page header ----- */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div
            className="text-[11px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            Mis diseños · {MODULE_LABELS[module]}
          </div>
          <h1 className="mt-0.5 text-3xl" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
            {MODULE_LABELS[module]}
          </h1>
          {description && (
            <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {description}
            </p>
          )}
        </div>
        <Link to={`/app/${routeFor(module)}/new`} className="no-underline">
          <Button variant="primary" size="md">
            <Plus size={14} strokeWidth={2} /> Nuevo cálculo
          </Button>
        </Link>
      </div>

      {/* ----- Search bar ----- */}
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            strokeWidth={1.8}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }}
          />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.currentTarget.value);
              setPage(1); // reset to first page when changing the filter
            }}
            placeholder="Buscar por nombre, proyecto o descripción…"
            className="h-10 w-full rounded-[var(--radius)] border bg-[var(--surface-2)] pl-9 pr-9 text-sm focus:border-[var(--accent)] focus:outline-none"
            style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
          />
          {q && (
            <button
              type="button"
              onClick={() => {
                setQ('');
                setPage(1);
              }}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-[var(--radius-sm)] p-1 hover:bg-[var(--surface-3)]"
            >
              <X size={12} strokeWidth={1.8} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
        <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
          {listQuery.isFetching ? 'Cargando…' : `${total} ${total === 1 ? 'diseño' : 'diseños'}`}
        </span>
      </div>

      {/* ----- Content ----- */}
      {listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
          <Loader2 size={20} className="mr-2 animate-spin" />
          Cargando diseños…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          module={module}
          searching={!!q}
          icon={icon}
          onClear={() => setQ('')}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((d) => (
            <DesignCard
              key={d.id}
              design={d}
              onOpen={() => navigate(`/app/${routeFor(module)}/${d.id}`)}
              onDelete={() => setConfirmDelete({ id: d.id, name: d.name })}
            />
          ))}
        </div>
      )}

      {/* ----- Pagination ----- */}
      {pages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Página anterior"
          >
            <ChevronLeft size={14} strokeWidth={1.8} />
          </Button>
          <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
            Página {page} de {pages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Página siguiente"
          >
            <ChevronRight size={14} strokeWidth={1.8} />
          </Button>
        </div>
      )}

      {/* ----- Delete confirm ----- */}
      {confirmDelete && (
        <ConfirmDeleteOverlay
          name={confirmDelete.name}
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => {
            await del.mutateAsync(confirmDelete.id);
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function DesignCard({
  design,
  onOpen,
  onDelete,
}: {
  design: {
    id: number;
    name: string;
    description: string;
    project: string;
    summary: string;
    subtype: string | null;
    updated_at: string;
  };
  onOpen: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="group relative rounded-[var(--radius-md)] border p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--accent)]"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        title="Abrir diseño"
      >
        <div className="mb-1 flex items-center gap-2">
          {design.subtype && (
            <span
              className="rounded-[var(--radius-pill)] px-2 py-0.5 text-[9px] uppercase tracking-wider"
              style={{ background: 'rgba(245,158,11,0.18)', color: 'var(--accent)' }}
            >
              {design.subtype}
            </span>
          )}
          {design.project && (
            <span
              className="rounded-[var(--radius-pill)] border px-2 py-0.5 text-[9px] uppercase tracking-wider"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-muted)' }}
            >
              {design.project}
            </span>
          )}
        </div>
        <h3
          className="truncate text-base"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--text-primary)' }}
        >
          {design.name}
        </h3>
        {design.summary && (
          <div className="mt-1 font-mono tabular text-[12px]" style={{ color: 'var(--accent)' }}>
            {design.summary}
          </div>
        )}
        {design.description && (
          <p
            className="mt-2 line-clamp-2 text-[12.5px]"
            style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}
          >
            {design.description}
          </p>
        )}
        <div className="mt-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>
          Modificado {new Date(design.updated_at).toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute right-3 top-3 rounded-[var(--radius-sm)] p-1.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-[var(--surface-2)]"
        aria-label="Eliminar diseño"
        title="Eliminar"
      >
        <Trash2 size={13} strokeWidth={1.8} style={{ color: 'var(--danger)' }} />
      </button>
    </div>
  );
}

function EmptyState({
  module,
  searching,
  icon,
  onClear,
}: {
  module: DesignModule;
  searching: boolean;
  icon?: React.ReactNode;
  onClear: () => void;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-[var(--radius-md)] border p-14 text-center"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div
        className="mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}
      >
        {icon ?? <FolderOpen size={24} strokeWidth={1.5} />}
      </div>
      <h3 className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        {searching ? 'Sin coincidencias' : 'Aún no tienes diseños guardados'}
      </h3>
      <p
        className="mt-2 max-w-md text-[12.5px]"
        style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}
      >
        {searching
          ? 'Intenta otra búsqueda o limpia el filtro para ver todos.'
          : 'Crea uno nuevo y guárdalo desde el botón "Guardar" para verlo aquí la próxima vez.'}
      </p>
      <div className="mt-5 flex gap-2">
        {searching && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Limpiar búsqueda
          </Button>
        )}
        <Link to={`/app/${routeFor(module)}/new`} className="no-underline">
          <Button variant="primary" size="sm">
            <Plus size={14} strokeWidth={2} /> Nuevo cálculo
          </Button>
        </Link>
      </div>
    </div>
  );
}

function ConfirmDeleteOverlay({
  name,
  busy,
  onCancel,
  onConfirm,
}: {
  name: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <div
        className="w-[92vw] max-w-md rounded-[var(--radius-lg)] border p-6 shadow-2xl"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="text-lg"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
        >
          ¿Eliminar este diseño?
        </h3>
        <p className="mt-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
          Se eliminará permanentemente <strong>{name}</strong>. Esta acción no se puede deshacer.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onCancel}>
            Cancelar
          </Button>
          <Button variant="danger" size="md" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.8} />}
            Eliminar
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Map a `DesignModule` to its URL prefix. Most are identical, but the
 * frontend uses `/app/footings` instead of `/app/zapatas` and
 * `/app/slabs` instead of `/app/losas` and `/app/walls` for muros.
 *
 * Análisis modules live under `/app/analysis/<thing>` so they don't
 * collide with the Diseño routes.
 */
function routeFor(module: DesignModule): string {
  switch (module) {
    case 'zapatas':
      return 'footings';
    case 'losas':
      return 'slabs';
    case 'muros':
      return 'walls';
    case 'analysis_beams':
      return 'analysis/beams';
    case 'analysis_columns':
      return 'analysis/columns';
    case 'analysis_losas':
      return 'analysis/slabs';
    case 'analysis_muros':
      return 'analysis/walls';
    case 'analysis_zapatas':
      return 'analysis/footings';
    default:
      return module;
  }
}
